import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Users, ArrowRight, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const Delegations = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    delegated_to: '',
    amount: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [groupId]);

  const fetchData = async () => {
    try {
      const [groupRes, delegationsRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}`),
        axios.get(`${API}/delegations/group/${groupId}`)
      ]);
      
      setGroup(groupRes.data);
      setDelegations(delegationsRes.data);

      // Fetch member details
      const memberPromises = groupRes.data.member_ids.map(id => 
        axios.get(`${API}/users/${id}`)
      );
      const membersData = await Promise.all(memberPromises);
      setMembers(membersData.map(res => res.data));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDelegation = async (e) => {
    e.preventDefault();
    if (!formData.delegated_to || !formData.amount) {
      toast.error('Please fill all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/delegations?user_id=${user.id}`, {
        group_id: groupId,
        delegated_to: formData.delegated_to,
        amount: parseFloat(formData.amount)
      });
      toast.success('Delegation created!');
      setShowCreateDialog(false);
      setFormData({ delegated_to: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create delegation');
    } finally {
      setSubmitting(false);
    }
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? member.name : 'Unknown';
  };

  const calculateOwed = () => {
    const owed = {};
    delegations.forEach(delegation => {
      if (delegation.status === 'pending') {
        const key = `${delegation.delegated_by}-${delegation.delegated_to}`;
        if (!owed[key]) {
          owed[key] = {
            from: delegation.delegated_by,
            to: delegation.delegated_to,
            amount: 0
          };
        }
        owed[key].amount += delegation.amount;
      }
    });
    return Object.values(owed);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const owedSummary = calculateOwed();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/group/${groupId}`)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="delegations-title">
                Payment Delegations
              </h1>
              <p className="text-gray-600 mt-1">Who owes whom in {group.name}</p>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
            >
              Delegate Payment
            </Button>
          </div>

          {/* Who Owes Whom Summary */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border border-blue-200">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-blue-700" />
              <h2 className="text-lg font-semibold text-blue-900">Outstanding Balances</h2>
            </div>

            {owedSummary.length === 0 ? (
              <p className="text-blue-700 text-center py-4">All settled up! 🎉</p>
            ) : (
              <div className="space-y-3">
                {owedSummary.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-4 flex items-center justify-between border border-blue-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-700 font-semibold">
                          {getMemberName(item.from)[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{getMemberName(item.from)}</p>
                        <p className="text-sm text-gray-600">owes</p>
                      </div>
                    </div>

                    <ArrowRight className="w-5 h-5 text-gray-400" />

                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">{getMemberName(item.to)}</p>
                        <p className="text-sm text-gray-600">receives</p>
                      </div>
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-700 font-semibold">
                          {getMemberName(item.to)[0]}
                        </span>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">₹{item.amount.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* All Delegations History */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delegation History</h3>
            {delegations.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No delegations yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {delegations.map((delegation, index) => (
                  <div
                    key={delegation.id}
                    className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-200"
                  >
                    <div>
                      <p className="font-semibold text-gray-900">
                        {getMemberName(delegation.delegated_by)} → {getMemberName(delegation.delegated_to)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(delegation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">₹{delegation.amount}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        delegation.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {delegation.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Create Delegation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delegate Payment</DialogTitle>
            <DialogDescription>
              Ask another member to pay on your behalf
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateDelegation} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="delegated_to">Delegate To</Label>
              <Select
                value={formData.delegated_to}
                onValueChange={(value) => setFormData({ ...formData, delegated_to: value })}
              >
                <SelectTrigger className="rounded-xl mt-2">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {members
                    .filter(m => m.id !== user.id)
                    .map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="amount">Amount (₹)</Label>
              <Input
                id="amount"
                type="number"
                placeholder="Enter amount"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="rounded-xl mt-2"
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Delegation'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Delegations;
